import { useState, useCallback, useEffect } from 'react';
import { Button } from '../../components/Button';
import type { ProfileUser, ApiResponseWithUser } from '../../types/profile';

interface PersonalInfoSectionProps {
    user: ProfileUser;
    onUpdate: (data: Partial<ProfileUser>) => Promise<ApiResponseWithUser>;
    isLoading: boolean;
}

export function PersonalInfoSection({ user, onUpdate, isLoading }: PersonalInfoSectionProps) {
    const [formData, setFormData] = useState({
        name: user.name,
        lastname: user.lastname,
        cellphone: user.cellphone,
    });
    const [error, setError] = useState<string | null>(null);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        setFormData({
            name: user.name,
            lastname: user.lastname,
            cellphone: user.cellphone,
        });
        setIsDirty(false);
    }, [user]);

    const handleChange = useCallback((field: keyof typeof formData) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target?.value ?? '';
        setFormData(prev => ({ ...prev, [field]: value }));
        setIsDirty(true);
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!formData.name.trim()) { setError('El nombre es requerido'); return; }
        if (!formData.lastname.trim()) { setError('El apellido es requerido'); return; }
        if (!formData.cellphone.trim()) { setError('El teléfono es requerido'); return; }

        const result = await onUpdate(formData);
        if (result.success) {
            setIsDirty(false);
        } else {
            setError(result.message);
        }
    }, [formData, onUpdate]);

    return (
        <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Datos Personales</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="profile-email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                    </label>
                    <input
                        type="email"
                        id="profile-email"
                        className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2.5 text-gray-500 cursor-not-allowed"
                        value={user.email}
                        disabled
                    />
                    <span className="text-xs text-gray-400 mt-1 block">El email no se puede modificar</span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="profile-name" className="block text-sm font-medium text-gray-700 mb-1">
                            Nombre *
                        </label>
                        <input
                            type="text"
                            id="profile-name"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:border-green-500 disabled:bg-gray-50"
                            value={formData.name}
                            onChange={handleChange('name')}
                            placeholder="Juan"
                            autoComplete="given-name"
                            disabled={isLoading}
                        />
                    </div>
                    <div>
                        <label htmlFor="profile-lastname" className="block text-sm font-medium text-gray-700 mb-1">
                            Apellido *
                        </label>
                        <input
                            type="text"
                            id="profile-lastname"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:border-green-500 disabled:bg-gray-50"
                            value={formData.lastname}
                            onChange={handleChange('lastname')}
                            placeholder="Pérez"
                            autoComplete="family-name"
                            disabled={isLoading}
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="profile-cellphone" className="block text-sm font-medium text-gray-700 mb-1">
                        Teléfono *
                    </label>
                    <input
                        type="tel"
                        id="profile-cellphone"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:border-green-500 disabled:bg-gray-50"
                        value={formData.cellphone}
                        onChange={handleChange('cellphone')}
                        placeholder="+54 9 11 1234-5678"
                        autoComplete="tel"
                        disabled={isLoading}
                    />
                </div>

                {error && (
                    <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
                        {error}
                    </div>
                )}

                <Button type="submit" disabled={isLoading || !isDirty}>
                    {isLoading ? 'Guardando...' : 'Guardar cambios'}
                </Button>
            </form>
        </div>
    );
}
